import { Blocks, defineBlocksWithJsonArray } from 'blockly/core';

import {
  getAnalogPinOptions,
  getDefaultSerialBaud,
  getDigitalPinOptions,
  getLogicLevelOptions,
  getPinModeOptions,
  getPwmPinOptions,
  getSerialBaudOptions,
} from './board-context.js';

const ARDUINO_BLOCK_COLOR = 120;
const IO_BLOCK_COLOR = 210;
const SERIAL_BLOCK_COLOR = 30;

const arduinoBlockDefinitions = [
  {
    type: 'arduino_program',
    message0: 'Setup %1',
    args0: [{ type: 'input_statement', name: 'SETUP' }],
    message1: 'loop %1',
    args1: [{ type: 'input_statement', name: 'LOOP' }],
    style: 'arduino_hat_blocks',
    tooltip: 'Main program structure with setup and loop sections.',
    helpUrl: '',
  },
  {
    type: 'arduino_setup',
    message0: 'Setup %1',
    args0: [{ type: 'input_statement', name: 'SETUP' }],
    style: 'arduino_hat_blocks',
    tooltip: 'Code that runs once when the board starts.',
    helpUrl: '',
  },
  {
    type: 'arduino_loop',
    message0: 'Loop %1',
    args0: [{ type: 'input_statement', name: 'LOOP' }],
    style: 'arduino_hat_blocks',
    tooltip: 'Code that runs repeatedly.',
    helpUrl: '',
  },
  {
    type: 'arduino_delay',
    message0: 'delay %1 milliseconds',
    args0: [{ type: 'input_value', name: 'MS', check: 'Number' }],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_blocks',
    tooltip: 'Pause the program for a number of milliseconds.',
  },
  {
    type: 'arduino_delay_microseconds',
    message0: 'delayMicroseconds %1',
    args0: [{ type: 'input_value', name: 'US', check: 'Number' }],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_blocks',
    tooltip: 'Pause the program for a number of microseconds.',
  },
  {
    type: 'arduino_millis',
    message0: 'millis',
    output: 'Number',
    style: 'arduino_blocks',
    tooltip: 'Milliseconds since the board started.',
  },
  {
    type: 'arduino_micros',
    message0: 'micros',
    output: 'Number',
    style: 'arduino_blocks',
    tooltip: 'Microseconds since the board started.',
  },
  {
    type: 'arduino_pin_mode',
    message0: 'set pin %1 mode %2',
    args0: [
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getDigitalPinOptions,
      },
      {
        type: 'field_dropdown',
        name: 'MODE',
        options: getPinModeOptions,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_io_blocks',
    tooltip: 'Configure a digital pin as input or output.',
  },
  {
    type: 'arduino_digital_write',
    message0: 'digital write pin %1 %2',
    args0: [
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getDigitalPinOptions,
      },
      {
        type: 'field_dropdown',
        name: 'STATE',
        options: getLogicLevelOptions,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_io_blocks',
    tooltip: 'Write HIGH or LOW to a digital pin.',
  },
  {
    type: 'arduino_digital_read',
    message0: 'digital read pin %1',
    args0: [
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getDigitalPinOptions,
      },
    ],
    output: 'Boolean',
    style: 'arduino_io_blocks',
    tooltip: 'Read HIGH or LOW from a digital pin.',
  },
  {
    type: 'arduino_analog_read',
    message0: 'analog read pin %1',
    args0: [
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getAnalogPinOptions,
      },
    ],
    output: 'Number',
    style: 'arduino_io_blocks',
    tooltip: 'Read analog value (0-1023 on AVR, 0-4095 on ESP).',
  },
  {
    type: 'arduino_analog_write',
    message0: 'analog write (PWM) pin %1 value %2',
    args0: [
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getPwmPinOptions,
      },
      { type: 'input_value', name: 'VALUE', check: 'Number' },
    ],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_io_blocks',
    tooltip: 'Write PWM value to a pin.',
  },
  {
    type: 'arduino_serial_begin',
    message0: 'Serial begin %1 baud',
    args0: [
      {
        type: 'field_dropdown',
        name: 'BAUD',
        options: getSerialBaudOptions,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_serial_blocks',
    tooltip: 'Start serial communication at the given baud rate.',
  },
  {
    type: 'arduino_serial_print',
    message0: 'Serial print %1',
    args0: [{ type: 'input_value', name: 'TEXT', check: ['String', 'char'] }],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_serial_blocks',
    tooltip: 'Print text or a single character to the serial port without a newline.',
  },
  {
    type: 'arduino_serial_println',
    message0: 'Serial println %1',
    args0: [{ type: 'input_value', name: 'TEXT', check: ['String', 'char'] }],
    previousStatement: null,
    nextStatement: null,
    style: 'arduino_serial_blocks',
    tooltip: 'Print text or a single character to the serial port with a newline.',
  },
  {
    type: 'arduino_serial_available',
    message0: 'Serial available',
    output: 'Number',
    style: 'arduino_serial_blocks',
    tooltip: 'Number of bytes available to read from serial.',
  },
  {
    type: 'arduino_serial_read',
    message0: 'Serial read byte',
    output: 'Number',
    style: 'arduino_serial_blocks',
    tooltip: 'Read one byte from serial as a number (0-255).',
  },
  {
    type: 'arduino_serial_read_char',
    message0: 'Serial read character',
    output: 'char',
    style: 'arduino_serial_blocks',
    tooltip: 'Read one character from serial for comparisons and char variables.',
  },
  {
    type: 'arduino_high_low',
    message0: '%1',
    args0: [
      {
        type: 'field_dropdown',
        name: 'LEVEL',
        options: getLogicLevelOptions,
      },
    ],
    output: 'Boolean',
    style: 'arduino_io_blocks',
    tooltip: 'HIGH or LOW constant.',
  },
];

export function registerArduinoBlocks(): void {
  defineBlocksWithJsonArray(arduinoBlockDefinitions);
}

export function getDefaultSerialBaudField(): string {
  return String(getDefaultSerialBaud());
}

/** Refresh dynamic dropdown fields after board change */
export function refreshBoardSensitiveBlocks(): void {
  // Dropdown options use functions — Blockly re-evaluates on next render.
  // Force toolbox/workspace refresh from the host app when board changes.
  void Blocks;
}

export { ARDUINO_BLOCK_COLOR, IO_BLOCK_COLOR, SERIAL_BLOCK_COLOR };
